import { Icon20StarsFilled } from '@vkontakte/icons';
import { Headline, Text } from '@vkontakte/vkui';
import { ReactComponent as CloudRebrandingIcon } from 'img/icons/cloud_rebranding.svg';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { aiImageGenerationFreeLimit, aiImageGenerationPaidLimit } from 'reactApp/appHelpers/featuresHelpers/features/aiImageGeneration';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import { canMakeAiGenerationRequest } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.helpers';
import {
    getAiGenerationImage,
    getAiGenerationImageSize,
    getAiGenerationImageState,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';
import { EAiPhotoGenerationState } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.types';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { AiGenerationClamp } from 'reactApp/ui/AiPhotoGeneration/AiGenerationClamp/AiGenerationClamp';
import { FullScreenAiPhotoGenerationLoader } from 'reactApp/ui/AiPhotoGeneration/Loader/FullScreenAiPhotoGenerationLoader';
import { AiPhotoGenerationToolbarComplete } from 'reactApp/ui/AiPhotoGeneration/ToolbarComplete/AiPhotoGenerationToolbarComplete';
import { AiPhotoGenerationToolbarIdle } from 'reactApp/ui/AiPhotoGeneration/ToolbarIdle/AiPhotoGenerationToolbarIdle';
import { Screen } from 'reactApp/ui/Screen/Screen';
import { opener } from 'reactApp/utils/opener';

import styles from './FullScreenAiPhotoGeneration.css';

interface IProps {
    onClose?: () => void;
    onShow?: () => void;
}

const onClickIcon = () => {
    opener('/home', true);
};

export const FullScreenAiPhotoGeneration = memo<IProps>(({ onClose, onShow }) => {
    const state = useSelector(getAiGenerationImageState);
    const image = useSelector(getAiGenerationImage);
    const size = useSelector(getAiGenerationImageSize);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const limit = isPaidUser || isCorpUser ? aiImageGenerationPaidLimit : aiImageGenerationFreeLimit;
    const canMakeReguest = canMakeAiGenerationRequest(limit);

    const onCloseGenerator = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const content = useMemo(() => {
        if (state === EAiPhotoGenerationState.idle && !canMakeReguest) {
            return <AiGenerationClamp onClose={onCloseGenerator} />;
        } else if (state === EAiPhotoGenerationState.idle) {
            return <Icon20StarsFilled className={styles.idleIcon} width={116} height={116} data-test-id="idle-icon" />;
        } else if (state === EAiPhotoGenerationState.proccess) {
            return <FullScreenAiPhotoGenerationLoader />;
        }
        return <img src={image} className={styles.generatedImage} alt="Сгенерированная картинка" />;
    }, [state, image, canMakeReguest, onCloseGenerator]);

    const toolbar = useMemo(() => {
        if (state === EAiPhotoGenerationState.idle && !canMakeReguest) {
            return null;
        } else if (state === EAiPhotoGenerationState.complete) {
            return <AiPhotoGenerationToolbarComplete />;
        } else if (state === EAiPhotoGenerationState.idle) {
            return <AiPhotoGenerationToolbarIdle />;
        }

        return null;
    }, [state, canMakeReguest]);

    const titleText = useMemo(() => {
        if (state === EAiPhotoGenerationState.complete) {
            return 'Сгенерированное изображение';
        } else if (state === EAiPhotoGenerationState.proccess) {
            return 'Генерация изображения с нейросетью';
        }

        return 'Сгенерированное изображение';
    }, [state]);

    useEffect(() => {
        onShow?.();
    }, []);

    return (
        <Screen
            onClose={onCloseGenerator}
            withLogo={false}
            aboveAll={false}
            className={VK_UI_DARK_THEME}
            contentClassName={styles.screenContent}
            closeIconClassName={styles.screenCloseIcon}
        >
            <div className={styles.root} data-test-id="fullscreen-ai-photo-generation">
                <div className={styles.header} data-test-id="headers">
                    <CloudRebrandingIcon
                        width={32}
                        height={32}
                        className={styles.cloudIcon}
                        data-test-id="cloud-icon"
                        onClick={onClickIcon}
                    />
                    <div className={styles.separator} />
                    <div className={styles.titleContainer}>
                        <Headline level="1" weight="1" className={styles.title}>
                            {titleText}
                        </Headline>
                        {state === 'complete' && size && (
                            <Text weight="1" className={styles.size}>
                                {size.value}
                            </Text>
                        )}
                    </div>
                </div>
                <div className={styles.mainContent} data-test-id="main-content">
                    {content}
                </div>

                {toolbar}
            </div>
        </Screen>
    );
});

export const renderFullScreenAiPhotoGeneration = ({ onClose, ...data }: IProps) => {
    openPopupHelper({ popupName: popupNames.FULLSCREEN_AI_GENERATION_IMAGE, data, onClose });
};

export const closeFullScreenAiPhotoGeneration = () => closePopupHelper(popupNames.FULLSCREEN_AI_GENERATION_IMAGE);

FullScreenAiPhotoGeneration.displayName = 'FullScreenAiPhotoGeneration';
